import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { WorldSlug } from '@vrlearning/lesson-schema';
import type { SoundPlan } from '../audio/plan.js';
import type { HaloSource } from '../look/halos.js';
import type { FloatImage } from '../look/sky.js';
import type { ShadowArea } from '../look/sun.js';
import { fbm } from '../look/noise.js';
import { WorldLoader } from './loader.js';
import {
  MANIFEST,
  modelFile,
  textureFile,
  worldUrl,
  type Manifest,
  type ManifestFile,
} from './manifest.js';

/**
 * A world: a real-looking place around the stage (ADR-032). Each world is a
 * definition -- its sky and light, its props as data, and a `build` that
 * makes its ground, water and air in code -- and a `WorldBuilder` turns it
 * into a scene graph at once, with every surface in its fallback colour,
 * the files filling in as they arrive.
 *
 * Zones, which the budget test holds a world to: the **ground** of the stage
 * and apron (flat at y = 0 within 8.5 m, receives the shadow); **dressing**,
 * everything solid beyond (no shadow, never a ray's target, nothing above
 * 5 cm within 8.5 m); and **air** -- light shafts, motes, smoke -- which may
 * drift anywhere, because nothing can stand in it.
 */

export type Placement = {
  readonly pos: readonly [number, number, number];
  /** Degrees about y. */
  readonly turn?: number;
  readonly scale?: number;
  /** Degrees about x and z: a boulder settled at an angle. */
  readonly tilt?: readonly [number, number];
};

export type PropPlan = {
  readonly asset: string;
  readonly at: readonly Placement[];
  /** Multiplied into its colours: basalt from a moon rock. */
  readonly tint?: THREE.ColorRepresentation;
  /** One draw call per material for all its copies, rather than one per copy. */
  readonly instanced?: boolean;
  /** Its model's units, when they are not metres (Poly Haven's shelves are in decimetres). */
  readonly unit?: number;
  /** Dull, whatever its maps say: a wet pebble made boulder-sized reads as plastic. */
  readonly matte?: boolean;
  /**
   * A plant (vegetation, 28 Sep 2026): its cut-out leaves drawn with alpha to
   * coverage from both sides, and swaying up to `sway` metres (in the model's
   * units) at its top, each copy in its own phase.
   */
  readonly foliage?: { readonly sway: number };
  /** One part of a set (Poly Haven's plants come four ferns to a file), by its node's name. */
  readonly variant?: string;
  /** Where the model's origin is taken: its own, or under the middle of its footprint. */
  readonly origin?: 'model' | 'footprint';
};

export type WorldDefinition = {
  readonly slug: WorldSlug;
  /** What it sounds like (world audio, ADR-034): part of the world, never of a lesson. */
  readonly sound: SoundPlan;
  readonly sky: {
    readonly hdri: string;
    /** Seen as the background (the island), or only lighting (the lab). */
    readonly seen: boolean;
    readonly exposure: number;
    readonly environment: number;
    /** Degrees the whole sky is turned about y, to put the sun where the world wants it. */
    readonly yaw: number;
    /** Degrees a minute the clouds drift (the background turns; light and shadow stay). */
    readonly drift: number;
  };
  readonly sun: {
    readonly intensity: number;
    readonly colour: THREE.ColorRepresentation;
    /** Where it shines from; absent, from the sky's own sun as the import measured it. */
    readonly from?: { readonly azimuth: number; readonly elevation: number };
  };
  /** Haze: its density, in the sky's horizon colour unless it has its own (under water, part 2). */
  readonly fog?: { readonly density: number; readonly colour?: THREE.ColorRepresentation };
  /** Emissive intensity of a lesson's glowing parts here: 1.6 in daylight. */
  readonly glow: number;
  /** When the sky is not seen: the colour behind everything. */
  readonly background?: THREE.ColorRepresentation;
  /** What the one shadow map covers; the stage's cylinder unless said (the villa's house, ADR-037). */
  readonly shadow?: ShadowArea;
  /**
   * A world that is not a 6 m stage (`staged: false` in lesson-schema's WORLDS, the villa): where
   * its pupils stand and carry things, which the budget test holds it to in place of the stage.
   */
  readonly walkable?: WalkablePlan;
  /**
   * The land's height at (x, z), metres: what a lesson's library model stands on as it walks
   * (render/models/runtime.ts), the same function the world's ground mesh is built from. 0 on the
   * stage and apron of every world; absent, the world is flat at y = 0 everywhere a model goes.
   * A T. rex placed at y = 0 floated over the island's slopes (Pieter, on a Quest 3, 5 Oct 2026).
   */
  readonly ground?: (x: number, z: number) => number;
  /**
   * Whether the stage's floor shows no sun shadow now: a package world's ground-projected 360°
   * photo, unlit (render/worlds/unlit-floor.ts). Its library models then stand in their contact
   * shadows (render/models/contact-shadow.ts). Absent: the floor is lit, as every code world's.
   */
  readonly unlitFloor?: () => boolean;
  readonly props: readonly PropPlan[];
  build(world: WorldBuilder): void;
};

/**
 * The rules a world with rooms is held to (budget.ts, ADR-037): its floor flat at y = 0 over the
 * walkable rectangles; no scenery standing on them (walls sit on their edges) but the fittings named;
 * room round every viewpoint.
 */
export type WalkablePlan = {
  readonly rects: readonly {
    readonly x: readonly [number, number];
    readonly z: readonly [number, number];
  }[];
  /** Where a pupil stands, on the floor. */
  readonly views: readonly (readonly [number, number, number])[];
  /** Meshes whose names start with this may stand on the floor: the fixed fittings. */
  readonly fittings: string;
  /** The ceiling: scenery from here up is out of everybody's way. */
  readonly ceiling: number;
};

/** How files are fetched and decoded; tests and the budget pass their own. */
export type Fetchers = {
  texture(url: string): Promise<THREE.Texture>;
  model(url: string): Promise<THREE.Object3D>;
  hdri(url: string): Promise<FloatImage>;
};

export type SurfaceOptions = {
  readonly asset: string;
  readonly maps: readonly ('diff' | 'nor_gl' | 'arm')[];
  readonly tint?: THREE.ColorRepresentation;
  readonly roughness?: number;
  readonly metalness?: number;
  readonly normalScale?: number;
  readonly vertexColors?: boolean;
  readonly side?: THREE.Side;
  /** A texture repeat in the material rather than in the geometry's own uv. */
  readonly repeat?: readonly [number, number];
  /**
   * Grass over it (greener hills, 28 Sep 2026): another texture set's diffuse,
   * laid from above in `tile` metres, mixed in by the mesh's `cover` attribute
   * (cover.ts); its average colour until it arrives.
   */
  readonly overlay?: {
    readonly asset: string;
    readonly tile: number;
    readonly tint?: THREE.ColorRepresentation;
  };
  /**
   * The photo's repeat broken up (the forest floor, Pieter's Quest check of
   * 29 Sep 2026): per pixel, by a noise laid in world metres (`tile`), the
   * photo is mixed with itself `second` times the scale and turned (`mix`,
   * 0–1), shaded lighter and darker by `tint` over a few tiles, and the
   * overlay's cover made patchy by `cover` (0–1).
   */
  readonly macro?: {
    readonly tile: number;
    readonly second: number;
    readonly mix: number;
    readonly tint: number;
    readonly cover?: number;
  };
};

/** Low-frequency noise that tiles, `size`², for a surface's macro variation. */
function macroNoise(size = 64, seed = 71): THREE.DataTexture {
  const cells = 4;
  const noise = fbm(seed, 3, cells);
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const v = Math.round(noise((x / size) * cells, (y / size) * cells) * 255);
      data.set([v, v, v, 255], (y * size + x) * 4);
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

/** A piece of shader for a surface: its uniforms and what it adds where. */
type Patch = {
  readonly key: string;
  readonly uniforms: Record<string, THREE.IUniform>;
  readonly vertexHead: string;
  readonly vertexBody: string;
  readonly fragmentHead: string;
  /** After the photo is sampled (map_fragment). */
  readonly afterMap?: string;
  /** After the vertex colours (color_fragment). */
  readonly afterColour?: string;
};

/**
 * One of a world's features a lesson can set a state of (ADR-032, "World
 * features and states"): `animate` when the class moved on by exactly one
 * step, at once otherwise.
 */
export type FeatureControl = { set(state: string, animate: boolean): void };

export class WorldBuilder {
  readonly root = new THREE.Group();
  readonly ground: THREE.Mesh[] = [];
  readonly dressing: THREE.Object3D[] = [];
  readonly air: THREE.Object3D[] = [];
  readonly halos: HaloSource[] = [];
  /** The world's features, by the name a lesson uses (lesson-schema's WORLDS). */
  readonly features = new Map<string, FeatureControl>();
  /**
   * How strong the world's sun and sky light are now, as a share of what its
   * definition says: a feature may dim them (the museum's evening, part 2).
   */
  readonly light = { sun: 1, environment: 1 };
  /** Each light block's shares of sun and sky: the world's light is their product (light blocks multiply). */
  readonly lightShares: { sun: number; sky: number }[] = [];
  /** Lamps that really light, over every light block: the cap is the world's (BLOCK_CAPS.lights). */
  litLamps = 0;
  /** Seconds of wind, for the plants' sway (placeProp): ambient, so never held. */
  readonly wind: THREE.IUniform<number> = { value: 0 };
  /**
   * What is seen of each feature: tested against where lesson-schema's
   * WORLDS says it is (its round), which is where a lesson turns the pupil.
   */
  readonly featureAnchors = new Map<string, THREE.Object3D>();
  /** Every served file this world asked for, by stored name. */
  readonly files = new Set<string>();
  /** Textures made in code, for the budget: bytes on the GPU. */
  codeTextureBytes = 0;
  readonly #frames: ((dt: number, elapsed: number, camera: THREE.Camera) => void)[] = [];
  readonly #featureFrames: ((dt: number, elapsed: number) => void)[] = [];

  constructor(
    readonly slug: WorldSlug,
    readonly loader: WorldLoader,
    readonly fetchers: Fetchers | undefined,
    readonly manifest: Manifest = MANIFEST,
    /** Where a stored file is fetched from: `/worlds/` for the code worlds, a package's own URLs. */
    readonly urlOf: (stored: string) => string = (stored) => worldUrl(stored),
  ) {
    this.root.name = `world:${slug}`;
  }

  /** The stage's ground: receives the shadow. */
  addGround(mesh: THREE.Mesh): THREE.Mesh {
    mesh.receiveShadow = true;
    mesh.castShadow = false;
    mesh.raycast = (): void => {};
    this.ground.push(mesh);
    this.root.add(mesh);
    return mesh;
  }

  /** Scenery: neither casts nor receives, and is never pointed at. */
  addDressing<T extends THREE.Object3D>(object: T): T {
    object.traverse((node) => {
      node.castShadow = false;
      node.receiveShadow = false;
      node.raycast = (): void => {};
    });
    this.dressing.push(object);
    this.root.add(object);
    return object;
  }

  /** Light, motes and smoke: nothing solid, allowed anywhere. */
  addAir<T extends THREE.Object3D>(object: T): T {
    object.traverse((node) => {
      node.castShadow = false;
      node.receiveShadow = false;
      node.raycast = (): void => {};
    });
    this.air.push(object);
    this.root.add(object);
    return object;
  }

  onFrame(update: (dt: number, elapsed: number, camera: THREE.Camera) => void): void {
    this.#frames.push(update);
  }

  /**
   * A feature's effects, frame by frame: held -- given no time -- while the
   * view is black at a fade, as a lesson's motion is, so a volcano's build-up
   * starts as the view fades in. Water, sky and drifting air are `onFrame`.
   */
  onFeatureFrame(update: (dt: number, elapsed: number) => void): void {
    this.#featureFrames.push(update);
  }

  addFeature(name: string, control: FeatureControl, anchor: THREE.Object3D): void {
    this.features.set(name, control);
    this.featureAnchors.set(name, anchor);
  }

  addHalo(source: HaloSource): void {
    this.halos.push(source);
  }

  /** Records a code-made texture's size for the budget. */
  codeTexture<T extends THREE.DataTexture>(texture: T): T {
    const { width, height } = texture.image;
    this.codeTextureBytes += width * height * 4 * (texture.generateMipmaps ? 4 / 3 : 1);
    return texture;
  }

  update(dt: number, elapsed: number, camera: THREE.Camera, held = false): void {
    this.wind.value += dt;
    for (const frame of this.#frames) frame(dt, elapsed, camera);
    for (const frame of this.#featureFrames) frame(held ? 0 : dt, elapsed);
  }

  /**
   * A lit surface from one of the world's texture sets, in its average
   * colour (times the tint) until the maps arrive, and for good if they do not.
   */
  surface(options: SurfaceOptions): THREE.MeshStandardMaterial {
    const tint = new THREE.Color(options.tint ?? 0xffffff);
    const diff = textureFile(options.asset, 'diff', this.manifest);
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(diff.averageColour ?? '#808080').multiply(tint),
      roughness: options.roughness ?? 0.95,
      metalness: options.metalness ?? 0,
      vertexColors: options.vertexColors ?? false,
      side: options.side ?? THREE.FrontSide,
    });
    material.name = `${options.asset}`;
    const patches: Patch[] = [];
    if (options.macro) patches.push(this.#macro(material, options.macro));
    if (options.overlay) patches.push(this.#overlay(material, options.overlay, options.macro));
    if (patches.length > 0) patchShader(material, patches);
    for (const map of options.maps) {
      const file = textureFile(options.asset, map, this.manifest);
      this.#texture(file, (texture) => {
        const before = material.color.clone();
        if (options.repeat) texture.repeat.set(...options.repeat);
        if (map === 'diff') {
          texture.colorSpace = THREE.SRGBColorSpace;
          material.map = texture;
          material.color.copy(tint);
        } else if (map === 'nor_gl') {
          material.normalMap = texture;
          material.normalScale.setScalar(options.normalScale ?? 1);
        } else {
          material.aoMap = texture;
          material.roughnessMap = texture;
          material.metalnessMap = texture;
          material.roughness = options.roughness === undefined ? 1 : options.roughness;
          material.metalness = options.metalness === undefined ? 1 : options.metalness;
          material.aoMapIntensity = 0.8;
        }
        material.needsUpdate = true;
        for (const copy of copiesOf.get(material) ?? []) followMaps(copy, material, before);
      });
    }
    return material;
  }

  #macro(material: THREE.MeshStandardMaterial, macro: NonNullable<SurfaceOptions['macro']>): Patch {
    const noise = this.codeTexture(macroNoise());
    // Held only by the shader's uniforms: named here so the world frees it (build.ts).
    material.userData['macroNoise'] = noise;
    return {
      key: `macro:${macro.tile}:${macro.second}`,
      uniforms: {
        uMacroNoise: { value: noise },
        uMacroTile: { value: macro.tile },
        uMacroSecond: { value: macro.second },
        uMacroMix: { value: macro.mix },
        uMacroTint: { value: macro.tint },
        uMacroCover: { value: macro.cover ?? 0 },
      },
      vertexHead: 'varying vec2 vMacroWorld;\n',
      vertexBody: 'vMacroWorld = (modelMatrix * vec4(transformed, 1.0)).xz;',
      fragmentHead:
        'varying vec2 vMacroWorld;\nuniform sampler2D uMacroNoise;\nuniform float uMacroTile;\nuniform float uMacroSecond;\nuniform float uMacroMix;\nuniform float uMacroTint;\nuniform float uMacroCover;\n',
      afterMap: `
          float macroA = texture2D(uMacroNoise, vMacroWorld / uMacroTile).r;
          float macroB = texture2D(uMacroNoise, vMacroWorld / (uMacroTile * 0.43) + vec2(0.43, 0.17)).r;
          float macroPatch = smoothstep(0.38, 0.62, texture2D(uMacroNoise, vMacroWorld / (uMacroTile * 0.29) + vec2(0.71, 0.29)).r);
          #ifdef USE_MAP
            vec2 macroUv = mat2(0.8, 0.6, -0.6, 0.8) * vMapUv * uMacroSecond + vec2(0.31, 0.57);
            vec3 macroSecond = texture2D(map, macroUv).rgb * diffuse;
            diffuseColor.rgb = mix(diffuseColor.rgb, macroSecond, smoothstep(0.35, 0.65, macroA) * uMacroMix);
          #endif
          diffuseColor.rgb *= 1.0 + uMacroTint * (macroB - 0.5) * 2.0;`,
    };
  }

  #overlay(
    material: THREE.MeshStandardMaterial,
    overlay: NonNullable<SurfaceOptions['overlay']>,
    macro?: SurfaceOptions['macro'],
  ): Patch {
    const file = textureFile(overlay.asset, 'diff', this.manifest);
    const tint = new THREE.Color(overlay.tint ?? 0xffffff);
    const uniforms = {
      uOverlay: { value: null as THREE.Texture | null },
      uOverlayReady: { value: 0 },
      uOverlayTile: { value: overlay.tile },
      uOverlayTint: { value: tint },
      uOverlayColour: { value: new THREE.Color(file.averageColour ?? '#6e6a40').multiply(tint) },
    };
    this.#texture(file, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      uniforms.uOverlay.value = texture;
      uniforms.uOverlayReady.value = 1;
      // Held only by the shader's uniforms: named here so the world frees it (build.ts).
      material.userData['overlay'] = texture;
    });
    // With macro variation the cover is patchy per pixel, not only per vertex.
    const cover = macro?.cover
      ? 'clamp(vCover * mix(1.0, macroPatch * 1.6, uMacroCover), 0.0, 1.0)'
      : 'clamp(vCover, 0.0, 1.0)';
    return {
      key: `overlay:${overlay.asset}`,
      uniforms,
      vertexHead:
        'attribute float cover;\nvarying float vCover;\nvarying vec2 vOverlayUv;\nuniform float uOverlayTile;\n',
      vertexBody: `vCover = cover;
          vOverlayUv = (modelMatrix * vec4(transformed, 1.0)).xz / uOverlayTile;`,
      fragmentHead:
        'varying float vCover;\nvarying vec2 vOverlayUv;\nuniform sampler2D uOverlay;\nuniform float uOverlayReady;\nuniform vec3 uOverlayTint;\nuniform vec3 uOverlayColour;\n',
      afterColour: `
          vec3 overlay = uOverlayReady > 0.5
            ? texture2D(uOverlay, vOverlayUv).rgb * uOverlayTint
            : uOverlayColour;
          #ifdef USE_COLOR
            overlay *= vColor.rgb;
          #endif
          diffuseColor.rgb = mix(diffuseColor.rgb, overlay, ${cover});`,
    };
  }

  #texture(file: ManifestFile, apply: (texture: THREE.Texture) => void): void {
    this.files.add(file.stored);
    const fetchers = this.fetchers;
    if (!fetchers) return;
    this.loader.request(
      file.stored,
      () => fetchers.texture(this.urlOf(file.stored)),
      (texture) => {
        if (!texture) return;
        const own = texture.clone();
        own.wrapS = THREE.RepeatWrapping;
        own.wrapT = THREE.RepeatWrapping;
        own.anisotropy = 4;
        own.needsUpdate = true;
        apply(own);
      },
    );
  }

  /**
   * A prop from the world's model set, placed as the plan says; empty until
   * its model arrives, and left out if it does not.
   */
  prop(plan: PropPlan): THREE.Group {
    const group = new THREE.Group();
    group.name = `prop:${plan.asset}`;
    this.addDressing(group);
    const file = modelFile(plan.asset, this.manifest);
    this.files.add(file.stored);
    for (const stored of file.requires ?? []) this.files.add(stored);
    const fetchers = this.fetchers;
    if (!fetchers) return group;
    this.loader.request(
      file.stored,
      () => fetchers.model(this.urlOf(file.stored)),
      (model) => {
        if (!model) return;
        blendGlass(model);
        group.add(placeProp(model, plan, this.wind));
        group.traverse((node) => {
          node.castShadow = false;
          node.receiveShadow = false;
          node.raycast = (): void => {};
        });
      },
    );
    return group;
  }
}

/**
 * A model's glass blended at its own alpha, never transmissive
 * (KHR_materials_transmission, the wall clock's): for transmission three
 * renders the scene again for each camera -- each eye, in a headset -- into a
 * 4× multisampled half-float target the size of the view, with its depth and
 * mips. Measured in headless Chrome at 1280 × 713: 57 MB, beyond any world's
 * texture budget (budget.ts), and a second pass of the scene per eye.
 */
export function blendGlass(model: THREE.Object3D): void {
  model.traverse((node) => {
    const material = (node as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
    for (const m of Array.isArray(material) ? material : material ? [material] : []) {
      if (m instanceof THREE.MeshPhysicalMaterial && m.transmission > 0) {
        m.transmission = 0;
        m.transparent = true;
        m.needsUpdate = true;
      }
    }
  });
}

/** A surface's pieces of shader, in one `onBeforeCompile`: uniforms, then each piece where it goes. */
function patchShader(material: THREE.MeshStandardMaterial, patches: readonly Patch[]): void {
  material.onBeforeCompile = (shader) => {
    for (const patch of patches) Object.assign(shader.uniforms, patch.uniforms);
    shader.vertexShader =
      patches.map((p) => p.vertexHead).join('') +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>\n${patches.map((p) => p.vertexBody).join('\n')}`,
      );
    shader.fragmentShader =
      patches.map((p) => p.fragmentHead).join('') +
      shader.fragmentShader
        .replace(
          '#include <map_fragment>',
          `#include <map_fragment>\n${patches.map((p) => p.afterMap ?? '').join('\n')}`,
        )
        .replace(
          '#include <color_fragment>',
          `#include <color_fragment>\n${patches.map((p) => p.afterColour ?? '').join('\n')}`,
        );
  };
  const key = patches.map((p) => p.key).join('|');
  material.customProgramCacheKey = () => key;
}

/** A placement as a matrix: turned, tilted, scaled, in the model's own units. */
export function placementMatrix(at: Placement, unit = 1): THREE.Matrix4 {
  const [tx = 0, tz = 0] = at.tilt ?? [];
  const turn = new THREE.Euler(
    THREE.MathUtils.degToRad(tx),
    THREE.MathUtils.degToRad(at.turn ?? 0),
    THREE.MathUtils.degToRad(tz),
    'YXZ',
  );
  const s = (at.scale ?? 1) * unit;
  return new THREE.Matrix4().compose(
    new THREE.Vector3(...at.pos),
    new THREE.Quaternion().setFromEuler(turn),
    new THREE.Vector3(s, s, s),
  );
}

/**
 * A loaded model, its parts merged per material -- a chemistry set of 52
 * parts is 2 draw calls -- and placed once per placement, as instances when
 * the plan asks.
 */
export function placeProp(
  model: THREE.Object3D,
  plan: PropPlan,
  wind: THREE.IUniform<number> = { value: 0 },
): THREE.Group {
  model.updateMatrixWorld(true);
  const variant = plan.variant ? model.getObjectByName(plan.variant) : model;
  if (!variant) {
    // A model that is not the one the manifest measured: nothing is planted, the world goes on.
    console.warn(`world: ${plan.asset} has no variant ${plan.variant}`);
    return new THREE.Group();
  }
  // Poly Haven's plants are modelled off their origin: stand them on their footprint.
  const footprint = new THREE.Box3().setFromObject(variant);
  const shift =
    plan.origin === 'footprint'
      ? new THREE.Matrix4().makeTranslation(
          -(footprint.min.x + footprint.max.x) / 2,
          -footprint.min.y,
          -(footprint.min.z + footprint.max.z) / 2,
        )
      : new THREE.Matrix4();
  const base = plan.origin === 'footprint' ? 0 : footprint.min.y;
  const height = Math.max(1e-3, footprint.max.y - footprint.min.y);
  const byMaterial = new Map<THREE.Material, THREE.BufferGeometry[]>();
  variant.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    const material = node.material as THREE.Material;
    const geometry = (node.geometry as THREE.BufferGeometry)
      .clone()
      .applyMatrix4(node.matrixWorld)
      .applyMatrix4(shift);
    byMaterial.set(material, [...(byMaterial.get(material) ?? []), geometry]);
  });
  const out = new THREE.Group();
  const tint = plan.tint !== undefined ? new THREE.Color(plan.tint) : undefined;
  for (const [shared, geometries] of byMaterial) {
    // The file's model is shared by every plan that places it (four fern variants,
    // each its own plan): each plan tints, dulls and sways its own copy, once.
    const material = shared.clone();
    if (material instanceof THREE.MeshStandardMaterial) {
      if (tint) material.color.multiply(tint);
      if (plan.matte) {
        material.roughnessMap = null;
        material.roughness = 0.95;
        material.needsUpdate = true;
      }
    }
    if (plan.foliage) foliage(material, plan.foliage.sway, { wind, base, height });
    const merged = mergeGeometries(geometries, false);
    const pieces = merged ? [merged] : geometries;
    for (const geometry of pieces) {
      if (plan.instanced) {
        const mesh = new THREE.InstancedMesh(geometry, material, plan.at.length);
        plan.at.forEach((at, i) => mesh.setMatrixAt(i, placementMatrix(at, plan.unit)));
        mesh.computeBoundingSphere();
        out.add(mesh);
      } else {
        for (const at of plan.at) {
          const mesh = new THREE.Mesh(geometry, material);
          mesh.applyMatrix4(placementMatrix(at, plan.unit));
          out.add(mesh);
        }
      }
    }
  }
  return out;
}

/**
 * A plant's material (vegetation, 28 Sep 2026). Its leaves are cut out, never
 * blended: alpha to coverage under the headset's MSAA smooths the cut, with no
 * sorting, and both sides are drawn. It sways in the vertex shader: sideways by
 * up to `sway` at its top, growing with the square of the height, in a phase
 * taken from where each copy stands -- no CPU per plant, no extra draw call.
 */
/** The copies made of each material by copyMaterial: a surface's late maps reach them too. */
const copiesOf = new WeakMap<THREE.Material, THREE.Material[]>();

/**
 * A copy of a material for the blocks that act on a part (kleur, zichtbaar, route; blocks/parts.ts):
 * three's clone(), which leaves the material's own shader behind -- foliage()'s sway, a surface's
 * overlay -- so those are kept (the same uniforms, the same wind), and the maps a surface block's
 * textures bring after the copy was made reach it too (surface()).
 */
export function copyMaterial<T extends THREE.Material>(source: T): T {
  const copy = source.clone();
  // Bound to the source, as the shader's uniforms are its (foliage() and patchShader() use no `this`).
  if (Object.hasOwn(source, 'onBeforeCompile')) {
    copy.onBeforeCompile = source.onBeforeCompile.bind(source);
  }
  if (Object.hasOwn(source, 'customProgramCacheKey')) {
    copy.customProgramCacheKey = source.customProgramCacheKey.bind(source);
  }
  copiesOf.set(source, [...(copiesOf.get(source) ?? []), copy]);
  return copy;
}

/**
 * A surface's maps, arrived, on a copy of it: its colour too while the copy still has the colour the
 * surface had before (a kleur block's paint is kept).
 */
function followMaps(
  copy: THREE.Material,
  source: THREE.MeshStandardMaterial,
  before: THREE.Color,
): void {
  if (!(copy instanceof THREE.MeshStandardMaterial)) return;
  copy.map = source.map;
  copy.normalMap = source.normalMap;
  copy.normalScale.copy(source.normalScale);
  copy.aoMap = source.aoMap;
  copy.aoMapIntensity = source.aoMapIntensity;
  copy.roughnessMap = source.roughnessMap;
  copy.metalnessMap = source.metalnessMap;
  copy.roughness = source.roughness;
  copy.metalness = source.metalness;
  if (copy.color.equals(before)) copy.color.copy(source.color);
  copy.needsUpdate = true;
}

export function foliage(
  material: THREE.Material,
  sway: number,
  shape: { wind: THREE.IUniform<number>; base: number; height: number },
  /**
   * Where each copy's phase in the wind comes from: an instanced plant's (its copy's place), or, for
   * a baked mesh that is one object, each vertex's own place -- `{ phase: 'position' }`.
   */
  options: { phase?: 'instance' | 'position' } = {},
): void {
  if (material.alphaTest > 0) {
    material.alphaToCoverage = true;
    material.transparent = false;
  }
  material.side = THREE.DoubleSide;
  material.onBeforeCompile = (shader) => {
    shader.uniforms['uWind'] = shape.wind;
    shader.uniforms['uSway'] = { value: sway };
    shader.uniforms['uSwayBase'] = { value: shape.base };
    shader.uniforms['uSwayHeight'] = { value: shape.height };
    shader.vertexShader =
      'uniform float uWind;\nuniform float uSway;\nuniform float uSwayBase;\nuniform float uSwayHeight;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        float swayUp = clamp((position.y - uSwayBase) / uSwayHeight, 0.0, 1.0);
        #ifdef USE_INSTANCING
          float swayPhase = dot(instanceMatrix[3].xz, vec2(0.37, 0.61));
        #else
          float swayPhase = ${
            options.phase === 'position'
              ? 'dot((modelMatrix * vec4(position, 1.0)).xz, vec2(0.37, 0.61))'
              : '0.0'
          };
        #endif
        float swayBend = uSway * swayUp * swayUp;
        transformed.x += sin(uWind * 1.3 + swayPhase) * swayBend;
        transformed.z += cos(uWind * 1.1 + swayPhase * 1.7) * swayBend * 0.6;`,
      );
  };
  material.customProgramCacheKey = () =>
    options.phase === 'position' ? `foliage:${sway}:position` : `foliage:${sway}`;
  material.needsUpdate = true;
}
